import { AppNode, NodeType } from "@features/builder/graph/types"
import { ValidationError, getLabelFormatSchema } from "@utils/form"
import { createLagNodeLabel, isNodeLabelUnique } from "@utils/node/labelValidation"
import * as yup from "yup"

export type FormValues = yup.InferType<ReturnType<typeof getRenameDialogSchema>>

function areAllLabelsUnique(nodes: AppNode[]): yup.TestConfig<string | undefined, yup.AnyObject> {
  return {
    name: "is-node-name-unique",
    message: "This node name is already in use",
    test: (currentLabel, context) => {
      const currentForm = context.from?.[1]?.value || context.options.context // context for validation without form resolver
      const currentNodeId = context.parent?.id
      const lagValue: number | undefined = context.parent?.lagValue
      const fullCurrentLabel =
        lagValue !== undefined ? createLagNodeLabel(currentLabel, lagValue) : currentLabel

      const fromCtx = (currentForm?.labels as FormValues["labels"]).filter(
        (fieldNode) => fieldNode.id !== currentNodeId,
      )

      const existingLabels = [
        ...nodes.filter((n) => n.type !== NodeType.Group).map((n) => n.data.label),
        ...fromCtx.map((ctxNode) =>
          ctxNode.lagValue !== undefined
            ? createLagNodeLabel(ctxNode.label, ctxNode.lagValue)
            : ctxNode.label,
        ),
      ]

      return isNodeLabelUnique(fullCurrentLabel, existingLabels)
    },
  }
}

export const getRenameDialogSchema = (nodes: AppNode[]) =>
  yup.object({
    labels: yup
      .array()
      .of(
        yup.object({
          label: yup
            .string()
            .required(ValidationError.REQUIRED)
            .when("type", ([type], schema) => {
              if (type === NodeType.Group) {
                return schema
              }

              return getLabelFormatSchema(schema).test(areAllLabelsUnique(nodes))
            }),
          id: yup.string().required(ValidationError.REQUIRED),
          type: yup.string().oneOf(Object.values(NodeType)).required(ValidationError.REQUIRED),
          lagValue: yup.number().optional(),
        }),
      )
      .required(),
  })
